<i18n>
{
  "fr": {
    "title": "Questionnaire ESG",
    "question-count": "{answered}/{total, plural, 1 {1&nbsp;question} other {#&nbsp;questions}}",
    "mark-step-as-completed": "Marquer cette étape comme complétée",
    "step-completed": "Cette étape est complétée",
    "questionnaire-empty": "Le questionnaire n'est pas encore prêt. Veuillez revenir plus tard."
  },
  "en": {
    "title": "ESG Questionnaire",
    "question-count": "{answered}/{total, plural, 1 {1&nbsp;question} other {#&nbsp;questions}}",
    "mark-step-as-completed": "Mark this step as completed",
    "step-completed": "This step is completed",
    "questionnaire-empty": "The questionnaire is not ready yet. Please come back later."
  }
}
</i18n>
<template>
  <div>
    <ui-title>
      {{ $t('title') }}
      <template v-slot:actions>
        <language-switch :currentQuestionnaireLanguage.sync="currentQuestionnaireLanguage"></language-switch>
      </template>
    </ui-title>

    <div class="card" v-if="questionnaire">
      <div class="questionnaire is-flex-desktop" v-if="currentQuestionnaireStep">
        <div class="questionnaire__sidebar">
          <questionnaire-sidebar
            :questionnaire="questionnaire"
            :currentQuestionnaireLanguage.sync="currentQuestionnaireLanguage"
            :currentQuestionnaireStep.sync="currentQuestionnaireStep"
          />
        </div>

        <div class="questionnaire__content" style="flex: 1 0 0">
          <ui-heading>
            <template #suptitle>
              <h2 class="is-size-6">{{ currentQuestionnaireSection.name }}</h2>
            </template>
            <h3 class="is-size-3 has-text-weight-bold questionnaire__title">{{ currentQuestionnaireStep.name }}</h3>
            <template #details>
              <b-tag
                v-if="currentQuestionnaireStep.groups"
                class="
                  is-medium
                  has-background-grey-light has-text-white
                  mt-8px
                  is-size-7 is-uppercase
                  has-text-weight-semibold has-letter-spacing-wide
                "
                v-html="getStepProgressHtml(currentQuestionnaireStep)"
              />
            </template>
          </ui-heading>

          <question-group
            v-for="group in currentQuestionnaireStep.groups"
            :key="group.uniqueKey"
            :title="group.name"
            :questions="group.questions"
            :language="currentQuestionnaireLanguage"
          ></question-group>

          <ui-box class="pt-0 pb-48px">
            <b-button
              :key="`toggle-btn-${currentQuestionnaireStep.isCompleted}` /*Petit hack pour que le bouton ne reste pas focused après le click*/"
              @click="toggleStepStatus"
              type="is-primary"
              :outlined="!currentQuestionnaireStep.isCompleted"
            >
              <b-icon class="mr-1" pack="far" :icon="currentQuestionnaireStep.isCompleted ? 'check-square' : 'square'"></b-icon>
              {{ currentQuestionnaireStep.isCompleted ? $t('step-completed') : $t('mark-step-as-completed') }}
            </b-button>
          </ui-box>

          <ui-box class="questionnaire__sequence-nav-bar is-hidden-mobile py-24px">
            <sequence-nav-bar
              :previous="previousStepSequence"
              :next="nextStepSequence"
              @clickPrevious="currentQuestionnaireStep = previousStep"
              @clickNext="currentQuestionnaireStep = nextStep"
            ></sequence-nav-bar>
          </ui-box>
        </div>
      </div>

      <div v-else>
        <p>{{ $t('questionnaire-empty') }}</p>
      </div>
    </div>
  </div>
</template>
<script>
import { CONTENT_LANGUAGE_ENGLISH, CONTENT_LANGUAGE_FRENCH } from '@/consts/enums'
import BusinessService from '@/services/business'
import i18n from '@/helpers/i18n'

import Breadcrumbs from '@/mixins/breadcrumbs'

import UiBox from '@/components/ui/box'
import UiHeading from '@/components/ui/heading'
import UiTitle from '@/components/ui/title'

import LanguageSwitch from '@/components/questionnaire/language-switch'
import SequenceNavBar from '@/components/generic/sequence-nav-bar'
import QuestionnaireSidebar from '@/components/questionnaire/questionnaire-sidebar'
import QuestionGroup from '@/components/questionnaire/question-group'

export default {
  components: {
    LanguageSwitch,
    SequenceNavBar,
    QuestionnaireSidebar,
    QuestionGroup,
    UiBox,
    UiHeading,
    UiTitle
  },
  mixins: [Breadcrumbs],
  data() {
    return {
      questionnaire: null,
      currentQuestionnaireLanguage: i18n.contentLanguage(),
      currentQuestionnaireStep: null
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('title')
        }
      ]
    },
    languageOptions() {
      return [
        { value: CONTENT_LANGUAGE_FRENCH, label: this.$t('language-fr') },
        { value: CONTENT_LANGUAGE_ENGLISH, label: this.$t('language-en') }
      ]
    },
    previousStep() {
      const allSteps = this.questionnaire.sections.flatMap((x) => x.steps)
      const currentStepIndex = allSteps.indexOf(this.currentQuestionnaireStep)

      if (currentStepIndex <= 0) return null

      return allSteps[currentStepIndex - 1]
    },
    nextStep() {
      const allSteps = this.questionnaire.sections.flatMap((x) => x.steps)
      const currentStepIndex = allSteps.indexOf(this.currentQuestionnaireStep)

      if (currentStepIndex < 0) return null
      if (currentStepIndex >= allSteps.length - 1) return null

      return allSteps[currentStepIndex + 1]
    },
    previousStepSequence() {
      return this.getSequenceFromStep(this.previousStep)
    },
    nextStepSequence() {
      return this.getSequenceFromStep(this.nextStep)
    },
    currentQuestionnaireSection() {
      return this.getSectionFromStep(this.currentQuestionnaireStep)
    }
  },
  methods: {
    getSequenceFromStep(step) {
      if (!step) return null

      const section = this.getSectionFromStep(step)

      return {
        label: `${section.name} – ${step.name}`
      }
    },
    getSectionFromStep(step) {
      return this.questionnaire.sections.find((x) => x.steps.indexOf(step) >= 0)
    },
    getStepProgressHtml(step) {
      if (step.isCompleted) {
        return `<i class='fas fa-check mr-8px'></i> ${this.$t('step-completed')}`
      }

      const allQuestions = step.groups.flatMap((x) => x.questions)
      const answeredQuestions = allQuestions.filter(({ answer }) => !!answer && answer.trim() !== '')

      return this.$t('question-count', {
        answered: answeredQuestions.length,
        total: allQuestions.length
      })
    },
    async toggleStepStatus() {
      await this.$apollo.mutate({
        mutation: this.$options.mutation.SetStepStatus,
        variables: {
          businessId: BusinessService.getCurrentBusiness().id,
          language: this.currentQuestionnaireLanguage,
          stepKey: this.currentQuestionnaireStep.uniqueKey,
          isCompleted: !this.currentQuestionnaireStep.isCompleted
        }
      })
    }
  },
  apollo: {
    questionnaireQuery: {
      query() {
        return this.$options.query.QuestionnaireData
      },
      variables() {
        return {
          dataLanguage: this.currentQuestionnaireLanguage,
          displayLanguage: i18n.contentLanguage(),
          businessId: BusinessService.getCurrentBusiness().id
        }
      },
      skip() {
        return !this.currentQuestionnaireLanguage
      },
      update({ questionnaire }) {
        this.questionnaire = questionnaire

        if (this.currentQuestionnaireStep) {
          const matchingStep = questionnaire.sections
            .flatMap((x) => x.steps)
            .find((x) => x.uniqueKey == this.currentQuestionnaireStep.uniqueKey)

          if (matchingStep) {
            this.currentQuestionnaireStep = matchingStep
            return
          }
        }

        if (questionnaire.sections.length > 0) {
          const firstIncompleteStep = questionnaire.sections.flatMap((x) => x.steps).find((x) => !x.isCompleted)
          const firstStep = questionnaire.sections[0].steps[0]

          this.currentQuestionnaireStep = firstIncompleteStep || firstStep
        }
      }
    }
  },
  watch: {
    currentQuestionnaireStep(newStep, previousStep) {
      if (previousStep && newStep.uniqueKey != previousStep.uniqueKey) {
        scrollTo(0, 0)
        this.$apollo.queries.questionnaireQuery.refetch()
      }
    }
  }
}
</script>
<graphql>
query QuestionnaireData($dataLanguage: ContentLanguage!, $displayLanguage: ContentLanguage!, $businessId: ID!) {
  questionnaire(language: $dataLanguage) {
    id
    sections {
      id
      uniqueKey
      name
      steps {
        id
        uniqueKey
        name
        isCompleted(businessId: $businessId)
        groups {
          id
          uniqueKey
          name
          questions {
            id
            uniqueKey
            label
            helpText
            type
            answer(businessId: $businessId)
            indicator(language: $displayLanguage, businessId: $businessId) {
              id
              language
              title
              # Ceci est un "fragment anonyme". Ça permet de lire les champs
              # spécifiques à ProjectIndicatorGraphType. Si l'indicateur est d'un
              # autre type, ils seront simplement null.
              ... on ProjectIndicatorGraphType {
                project {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
}

mutation SetStepStatus($businessId: ID!, $language: ContentLanguage!, $stepKey: String!, $isCompleted: Boolean!) {
  updateQuestionnaireStepStatus(
    input: { businessId: $businessId, language: $language, stepKey: $stepKey, isCompleted: $isCompleted }
  ) {
    step {
      id
      isCompleted(businessId: $businessId)
    }
  }
}
</graphql>

<style lang="scss" scoped>
.questionnaire {
  &__sidebar {
    @include desktop {
      width: 300px;
    }
    @include widescreen {
      width: 400px;
    }
  }

  &__content {
    border-left: 1px solid $grey-lighter;
  }

  &__title {
    line-height: 1.25;
  }

  &__sequence-nav-bar {
    border-top: 1px solid $grey-lighter;
  }
}
</style>